/**
 * /payroll-l2/expenses — Employee expense report queue.
 * Tabs: Draft | Submitted | Approved | Rejected | Reimbursed
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import { api } from "@/lib/api";
import type { ExpenseReport, ExpenseReportStatus } from "@/lib/types";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll-l2/expenses")({
  component: ExpensesPage,
});

const STATUS_COLOR: Record<ExpenseReportStatus, "default" | "primary" | "success" | "error" | "info"> = {
  draft: "default",
  submitted: "primary",
  approved: "success",
  rejected: "error",
  reimbursed: "info",
};

const TABS: { value: ExpenseReportStatus | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "submitted", label: "Submitted" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "reimbursed", label: "Reimbursed" },
];

function fmtMoney(v: string) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(v));
}

function ExpensesPage() {
  const [tab, setTab] = useState<ExpenseReportStatus | "all">("all");
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ["payroll-l2", "expenses", tab],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: "100" });
      if (tab !== "all") params.set("status", tab);
      return await api<ExpenseReport[]>(`/api/payroll-l2/expense-reports?${params}`);
    },
  });

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h5" fontWeight={600}>Expenses</Typography>
          <HelpHint docPath="payroll-l2/expenses.md" label="Expenses help" />
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onClick={() => navigate({ to: "/payroll-l2/expenses/new" as any })}
        >
          New Report
        </Button>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        {TABS.map((t) => <Tab key={t.value} value={t.value} label={t.label} />)}
      </Tabs>

      {isLoading && <CircularProgress size={24} />}
      {error && <Alert severity="error">Failed to load expense reports.</Alert>}

      {data && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell>Report Date</TableCell>
              <TableCell>Lines</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{r.employee_id.slice(0, 8)}</TableCell>
                <TableCell>{r.report_date}</TableCell>
                <TableCell>{r.lines?.length ?? 0}</TableCell>
                <TableCell>{fmtMoney(r.total_amount)}</TableCell>
                <TableCell>
                  <Chip label={r.status} color={STATUS_COLOR[r.status]} size="small" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
